import API from "../api.provider";

const attendanceService = {
  // ==================== Service Instances ====================

  /**
   * Get all service instances for the organization
   * @param {Object} params - Query parameters
   * @param {string} params.date - Specific date (YYYY-MM-DD) - will be used as both startDate and endDate
   * @param {string} params.startDate - Start date (YYYY-MM-DD)
   * @param {string} params.endDate - End date (YYYY-MM-DD)
   * @param {string} params.status - Filter by status
   * @param {boolean} params.includeMetrics - Include metrics data
   */
  getServiceInstances: async (params = {}) => {
    try {
      // If a single date is provided, use it as both startDate and endDate
      const queryParams = { ...params };
      if (params.date && !params.startDate && !params.endDate) {
        queryParams.startDate = params.date;
        queryParams.endDate = params.date;
        delete queryParams.date;
      }

      const response = await API.get("/api/v2/service-instances", { params: queryParams });
      return response.data;
    } catch (error) {
      console.error("Error fetching service instances:", error);
      throw error;
    }
  },

  /**
   * Get today's services
   */
  getTodaysServices: async () => {
    try {
      const response = await API.get("/api/v2/service-instances/today");
      return response.data;
    } catch (error) {
      console.error("Error fetching today's services:", error);
      throw error;
    }
  },

  /**
   * Get a single service instance by ID
   * @param {string} id - Service instance ID
   */
  getServiceInstanceById: async (id) => {
    try {
      const response = await API.get(`/api/v2/service-instances/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching service instance:", error);
      throw error;
    }
  },

  /**
   * Generate service instances for a date range
   * @param {Object} data
   * @param {string} data.startDate - Start date (YYYY-MM-DD)
   * @param {string} data.endDate - End date (YYYY-MM-DD)
   * @param {string} data.worshipServiceId - Optional: specific worship service ID
   */
  generateServiceInstances: async (data) => {
    try {
      const response = await API.post("/api/v2/service-instances/generate", data);
      return response.data;
    } catch (error) {
      console.error("Error generating service instances:", error);
      throw error;
    }
  },

  /**
   * Update a service instance
   * @param {string} id - Service instance ID
   * @param {Object} data - Update data
   */
  updateServiceInstance: async (id, data) => {
    try {
      const response = await API.put(`/api/v2/service-instances/${id}`, data);
      return response.data;
    } catch (error) {
      console.error("Error updating service instance:", error);
      throw error;
    }
  },

  /**
   * Delete a service instance
   * @param {string} id - Service instance ID
   */
  deleteServiceInstance: async (id) => {
    try {
      const response = await API.delete(`/api/v2/service-instances/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting service instance:", error);
      throw error;
    }
  },

  // ==================== Attendance Metrics ====================

  /**
   * Get metrics for a service instance
   * @param {string} serviceInstanceId - Service instance ID
   */
  getMetrics: async (serviceInstanceId) => {
    try {
      const response = await API.get(`/api/v2/attendance-metrics/${serviceInstanceId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching metrics:", error);
      throw error;
    }
  },

  /**
   * Create or update metrics (upsert)
   * @param {string} serviceInstanceId - Service instance ID
   * @param {Object} metrics - Metrics data
   * @param {number} metrics.attendance - Total attendance
   * @param {number} metrics.kids - Kids count
   * @param {number} metrics.salvations - Salvations count
   * @param {number} metrics.volunteers - Volunteers count
   * @param {number} metrics.contributions - Financial contributions
   * @param {string} metrics.notes - Optional notes
   * @param {Array} metrics.attachments - Optional attachments
   */
  upsertMetrics: async (serviceInstanceId, metrics) => {
    try {
      const response = await API.post(
        `/api/v2/attendance-metrics/${serviceInstanceId}`,
        {
          ...metrics,
          entry_source: "manual",
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error upserting metrics:", error);
      throw error;
    }
  },

  /**
   * Update specific metric fields
   * @param {string} serviceInstanceId - Service instance ID
   * @param {Object} metrics - Partial metrics data
   */
  updateMetrics: async (serviceInstanceId, metrics) => {
    try {
      const response = await API.patch(
        `/api/v2/attendance-metrics/${serviceInstanceId}`,
        metrics
      );
      return response.data;
    } catch (error) {
      console.error("Error updating metrics:", error);
      throw error;
    }
  },

  /**
   * Delete metrics for a service instance
   * @param {string} serviceInstanceId - Service instance ID
   */
  deleteMetrics: async (serviceInstanceId) => {
    try {
      const response = await API.delete(`/api/v2/attendance-metrics/${serviceInstanceId}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting metrics:", error);
      throw error;
    }
  },

  /**
   * Get metrics summary for a date range
   * @param {Object} params - Query parameters
   * @param {string} params.startDate - Start date (YYYY-MM-DD)
   * @param {string} params.endDate - End date (YYYY-MM-DD)
   */
  getMetricsSummary: async (params = {}) => {
    try {
      const response = await API.get("/api/v2/attendance-metrics/summary", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching metrics summary:", error);
      throw error;
    }
  },

  // ==================== Device Check ====================

  /**
   * Check if organization has devices
   */
  checkHasDevices: async () => {
    try {
      const response = await API.get("/api/v2/devices/has-devices");
      return response.data;
    } catch (error) {
      console.error("Error checking devices:", error);
      throw error;
    }
  },

  // ==================== Helper Functions ====================

  /**
   * Format date for API
   * @param {Date} date
   * @returns {string} YYYY-MM-DD
   */
  formatDate: (date) => {
    if (!date) return null;
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  },

  /**
   * Get date range for current month
   * @returns {Object} { startDate, endDate }
   */
  getCurrentMonthRange: () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    return {
      startDate: attendanceService.formatDate(startDate),
      endDate: attendanceService.formatDate(endDate),
    };
  },

  /**
   * Get date range for last N days
   * @param {number} days
   * @returns {Object} { startDate, endDate }
   */
  getLastNDaysRange: (days = 30) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    return {
      startDate: attendanceService.formatDate(startDate),
      endDate: attendanceService.formatDate(endDate),
    };
  },

  /**
   * Format time from 24-hour to 12-hour with AM/PM
   * @param {string} time - Time in HH:MM format
   * @returns {string} Time in 12-hour format
   */
  formatTime: (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  },

  /**
   * Format currency
   * @param {number} amount
   * @returns {string}
   */
  formatCurrency: (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  },

  /**
   * Get day name from day_of_week number
   * @param {number} dayNum - 0-6 (Sunday-Saturday)
   * @returns {string}
   */
  getDayName: (dayNum) => {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return days[dayNum] || "";
  },
};

export default attendanceService;
